/**
 * Datacode's own software products — the things we build, run and sell.
 * Single source of truth for /products index, detail pages, nav and SEO.
 * Copy lives in the page components; this file holds identity + metadata.
 */

export type ProductSlug = 'dilacrm' | 'dilaerp' | 'dilamenu';

export interface Product {
  slug: ProductSlug;
  /** Mono index shown on the index page ('01'). */
  index: string;
  name: string;
  /** Per-page accent — locked per DESIGN.md. */
  accent: string;
  /** One-line positioning used on cards and meta. */
  tagline: string;
  /** Two-sentence blurb for the index page. */
  blurb: string;
  /** Three mono stat chips on the index page. */
  chips: string[];
  /** Index-page visual. */
  image: string;
  imageAlt: string;
  /** Public URL of the live product (display + link). */
  domain: string;
  liveUrl: string;
  liveLabel: string;
  seo: {
    title: string;
    description: string;
    keywords: string[];
  };
}

export const products: Product[] = [
  {
    slug: 'dilacrm',
    index: '01',
    name: 'DilaCRM',
    accent: '#6b93ff',
    tagline: 'The AI inbox that answers before your competitor wakes up.',
    blurb:
      'WhatsApp, Instagram, Messenger, Telegram, TikTok and email in one inbox. AI drafts the reply in Kurdish, Arabic or English, you approve, it sends.',
    chips: ['6 channels', '5 languages', '24/7 AI'],
    image: '/images/products/dilacrm/hero-approval.webp',
    imageAlt:
      'DilaCRM inbox showing an AI-suggested reply awaiting human approval, with Send, Edit and Don’t send actions.',
    domain: 'crm.dilaweb.com',
    liveUrl: 'https://crm.dilaweb.com',
    liveLabel: 'Live inbox',
    seo: {
      title: 'DilaCRM: AI inbox for WhatsApp, Instagram, Telegram and more',
      description:
        'DilaCRM by Datacode gathers every customer channel into one AI inbox: WhatsApp, Instagram, Messenger, Telegram, TikTok and email, with Kurdish, Arabic and English AI replies, comment automation, WhatsApp campaigns, ads analytics and a built-in CRM.',
      keywords: [
        'DilaCRM',
        'SalesMate',
        'AI inbox Iraq',
        'WhatsApp CRM Kurdistan',
        'respond.io alternative',
        'Kurdish AI chatbot',
        'WhatsApp bulk campaigns',
        'comment automation Facebook Instagram',
        'Datacode',
      ],
    },
  },
  {
    slug: 'dilaerp',
    index: '02',
    name: 'DilaERP',
    accent: '#3cc984',
    tagline: 'The ERP that speaks Kurdish.',
    blurb:
      'Sales, stock, accounting, HR, payroll and POS in one simple system. Fifteen modules, three languages, self-hosted so your data stays yours.',
    chips: ['15 modules', '300 screens', '3 languages'],
    image: '/images/products/dilaerp/dashboard.webp',
    imageAlt:
      'DilaERP dashboard with monthly revenue, money owed, overdue invoices and a 30-day revenue chart in IQD.',
    domain: 'app.datacode.app',
    liveUrl: 'https://app.datacode.app',
    liveLabel: 'Live demo',
    seo: {
      title: 'DilaERP: the ERP that speaks Kurdish',
      description:
        'DilaERP by Datacode is a complete, simple ERP in Kurdish, Arabic and English: sales, inventory, accounting, HR and payroll, POS, CRM and 26+ reports, with an AI assistant and branded PDFs. Self-hosted, your data stays yours.',
      keywords: [
        'DilaERP',
        'Kurdish ERP',
        'ERP Iraq',
        'ERP Kurdistan',
        'ERPNext alternative',
        'Kurdish accounting software',
        'POS Iraq',
        'self-hosted ERP',
        'Datacode',
      ],
    },
  },
  {
    slug: 'dilamenu',
    index: '03',
    name: 'dila.menu',
    accent: '#d4a574',
    tagline: 'Digital menus, beautifully served.',
    blurb:
      'QR menus guests open in two seconds, WhatsApp and dine-in ordering that reaches your kitchen, and dila RMS, the POS built for Iraqi hospitality.',
    chips: ['38 venues', '11 themes', '<2s load'],
    image: '/images/products/dilamenu/menu-full.webp',
    imageAlt:
      'A dila.menu restaurant menu in the Kilim theme showing a chef’s signature Adana Kebab with IQD pricing and food photography.',
    domain: 'dila.menu',
    liveUrl: 'https://dila.menu',
    liveLabel: 'Live menus',
    seo: {
      title: 'dila.menu: digital menus and restaurant management for Kurdistan',
      description:
        'dila.menu by Datacode is Kurdistan’s digital menu platform: trilingual QR menus, WhatsApp ordering, reservations and live analytics, paired with dila RMS, the restaurant management system with table service, kitchen screens, Iraqi payments and Talabat integration.',
      keywords: [
        'dila.menu',
        'QR menu Kurdistan',
        'digital menu Iraq',
        'restaurant POS Iraq',
        'RMS restaurant management',
        'Talabat integration POS',
        'Kurdish restaurant software',
        'Datacode',
      ],
    },
  },
];

export function getProduct(slug: string): Product | undefined {
  return products.find((p) => p.slug === slug);
}
